import type { Locale } from '@seek/melways-sites';
import { useTranslations } from '@vocab/react';
import { Alert, Box, Heading, Notice, Stack, Text } from 'braid-design-system';
import type { GraphQLError } from 'graphql/error/GraphQLError';
import { observer } from 'mobx-react';
import { useState } from 'react';

import Loader from 'src/components/common/Loader';
import { TRACKING_CANDIDATE_DETAILS } from 'src/constants/pageTrackingData';
import type { PageConfig } from 'src/libs/PageConfig';
import type { INotes } from 'src/stores/JobApplicationStore/Notes';
import {
  trackAddAttachmentStart,
  trackAddedAttachment,
  trackDeleteAttachment,
} from 'src/utils/events';
import type { TrackStatusUpdateAttributes } from 'src/utils/eventsTypes';
import { sleep } from 'src/utils/sleep';

import {
  ApplicationAttachmentType,
  type ApplicationNoteV2,
} from '../../graphql/hirer-types.generated';

import translations from './.vocab';
import { AddNotesAttachments } from './AddNotesAttachment/AddNotesAttachment';
import {
  type Attachment,
  NotesAttachmentList,
} from './NotesAttachmentList/NotesAttachmentList';
import {
  useDeleteUploadedSupportDocMutation,
  type NotesAttachmentHistoryQuery,
  useNotesAttachmentHistoryQuery,
} from './NotesAttachments.hirer.generated';

import * as styles from '../NotesAttachments/Item/ItemContent/ItemContent.css';

// FIXME: Component uses generated types, define its own types and use them - ApplicationAttachmentV2, ApplicationNoteV2

interface NotesAttachmentsProps {
  notes: INotes;
  locale: Locale;
  isVerifiedId: TrackStatusUpdateAttributes['isVerifiedId'];
  jobId: string;
  applicationCorrelationId: string;
  jobApplicationId: string;
  seekCandidateId: string;
  adcentreCandidateId: string;
  addCustomError: PageConfig['dataDogRum']['addCustomError'];
  updateStoreWithNotesAttachmentData: (
    attachmentData: Attachment[],
    notesData: ApplicationNoteV2[],
  ) => void;
  userId: string;
  source: number;
}
const NotesAttachments = ({
  notes,
  locale,
  isVerifiedId,
  jobId,
  applicationCorrelationId,
  jobApplicationId,
  seekCandidateId,
  adcentreCandidateId,
  addCustomError,
  updateStoreWithNotesAttachmentData,
  userId,
  source,
}: NotesAttachmentsProps) => {
  const { t } = useTranslations(translations);

  // FIXME: This is a existing solution to show the error message for notes
  // This will be refactored in the follow up PR.
  const [showNoteError, setShowNoteError] = useState(false); // showNotesError
  const [addingNote, setAddingNote] = useState(false);

  const getCandidateSupportDocs = (queryData: NotesAttachmentHistoryQuery) =>
    queryData.applicationAttachmentsV2.__typename ===
    'ApplicationAttachmentsV2ResponseSuccess'
      ? queryData.applicationAttachmentsV2.result.filter(
          (attachment) =>
            attachment.attachmentType === ApplicationAttachmentType.OTHER,
        )
      : [];

  const getNotesData = (queryData: NotesAttachmentHistoryQuery) =>
    queryData.applicationNotesV2.__typename ===
    'ApplicationNotesResponseSuccess'
      ? queryData.applicationNotesV2.result
      : [];

  const {
    loading: notesAttachmentsLoading,
    data: notesAttachmentsData,
    error: apolloError,
    refetch,
  } = useNotesAttachmentHistoryQuery({
    variables: {
      applicationNotesInput: {
        applicationCorrelationId,
        jobId,
      },
      applicationAttachmentsV2Input: {
        applicationCorrelationId,
        jobId,
      },
    },
    onCompleted: (fetchedData) => {
      const allAttachments =
        fetchedData.applicationAttachmentsV2.__typename ===
        'ApplicationAttachmentsV2ResponseSuccess'
          ? fetchedData.applicationAttachmentsV2.result
          : [];

      const noteData = getNotesData(fetchedData);

      updateStoreWithNotesAttachmentData(allAttachments, noteData);
    },
    fetchPolicy: 'cache-and-network',
  });

  const getCandidateSupportDocCount = () =>
    (notesAttachmentsData &&
      getCandidateSupportDocs(notesAttachmentsData).length) ||
    0;

  const getNoteCount = () =>
    (notesAttachmentsData && getNotesData(notesAttachmentsData).length) || 0;

  const candidateSupportDocCount = getCandidateSupportDocCount();
  const noteCount = getNoteCount();
  const showNoteAttachmentHistory =
    candidateSupportDocCount > 0 || noteCount > 0 || Boolean(apolloError);

  const sendCustomError = (error: GraphQLError | any) =>
    addCustomError({
      error,
      context: {
        activity: 'DELETE_ATTACHMENT',
        description: 'Error when attempting: deleteUploadedSupportDoc',
      },
    });

  const [deleteUploadedSupportDoc] = useDeleteUploadedSupportDocMutation();

  const onDeleteAttachment = async (
    attachmentItem: Attachment,
  ): Promise<'done' | 'error'> => {
    try {
      const attachmentDeleted = await deleteUploadedSupportDoc({
        variables: {
          input: {
            applicationCorrelationId,
            attachmentId: attachmentItem.attachmentId,
            jobId,
          },
        },
      });

      if (
        attachmentDeleted.data &&
        attachmentDeleted.data.deleteUploadedSupportDoc.__typename ===
          'DeleteUploadedSupportDocResponseSuccess'
      ) {
        trackDeleteAttachment({
          applicationCorrelationId,
          applicationId: jobApplicationId,
          attachmentId: attachmentItem.attachmentId,
          seekCandidateId,
          adcentreCandidateId,
        });
        refetch();
        return 'done';
      }
      sendCustomError(attachmentDeleted.errors); // Handle response error
      return 'error';
    } catch (error) {
      sendCustomError(error); // Handle network error
      return 'error';
    }
  };

  // There are cases where after adding/deleting a note, the results from refetch are not updated immediately.
  // This delay is added to ensure that the results are updated before the user sees the changes.
  const UPDATE_NOTES_DELAY = 500;

  const onDeleteNote = async (
    attachmentItem: ApplicationNoteV2,
  ): ReturnType<typeof notes.removeNote> => {
    const deleteResponse = await notes.removeNote(
      attachmentItem.id,
      addCustomError,
    );

    await sleep(UPDATE_NOTES_DELAY);

    refetch();

    return deleteResponse;
  };

  // FIXME: This will call graphql mutaion
  const onAddNote = async (content: string) => {
    setAddingNote(true);
    const addNoteResponse = await notes.addNote(content, addCustomError);

    if (addNoteResponse === 'error') {
      setShowNoteError(true);
      return;
    }
    if (addNoteResponse === 'done') {
      await sleep(UPDATE_NOTES_DELAY);
      refetch();
    }
    setAddingNote(false);
  };

  const onAddAttachmentClick = () =>
    trackAddAttachmentStart({
      currentPage: TRACKING_CANDIDATE_DETAILS,
      applicationCorrelationId,
      applicationId: jobApplicationId,
      seekCandidateId,
      adcentreCandidateId,
    });

  const onAddAttachmentSuccess = () => {
    trackAddedAttachment({
      currentPage: TRACKING_CANDIDATE_DETAILS,
      applicationCorrelationId,
      applicationId: jobApplicationId,
      seekCandidateId,
      adcentreCandidateId,
    });

    refetch();
  };

  return (
    <Box marginBottom="medium" className={styles.notesAttachmentsContainer}>
      <Stack space="xlarge">
        <AddNotesAttachments
          showServerError={showNoteError}
          isLoading={addingNote}
          locale={locale}
          onAddNote={onAddNote}
          onAddAttachmentSuccess={onAddAttachmentSuccess}
          onAddAttachmentClick={onAddAttachmentClick}
          source={source}
          applicationCorrelationId={applicationCorrelationId}
          jobId={jobId}
          candidateSupportDocCount={candidateSupportDocCount}
          addCustomError={addCustomError}
        />

        {showNoteAttachmentHistory && (
          <Stack space="xxsmall">
            <Box paddingBottom="xxsmall">
              <Heading level="4">{t('Notes and attachments history')}</Heading>
            </Box>

            {(() => {
              if (notesAttachmentsLoading) {
                return <Loader />;
              }

              if (apolloError || !notesAttachmentsData) {
                return (
                  <Alert tone="critical">
                    <Text size="xsmall">
                      {t(
                        'We can’t show your notes and attachments right now. We’re doing our best to fix this. Try refreshing the page or check back later.',
                      )}
                    </Text>
                  </Alert>
                );
              }

              const attachmentResponseError =
                notesAttachmentsData.applicationAttachmentsV2.__typename ===
                'ResponseError';

              const notesResponseError =
                notesAttachmentsData.applicationNotesV2.__typename ===
                'ResponseError';

              if (attachmentResponseError && notesResponseError) {
                return (
                  <Alert tone="critical">
                    <Text size="xsmall">
                      {t(
                        'We can’t show your notes and attachments right now. We’re doing our best to fix this. Try refreshing the page or check back later.',
                      )}
                    </Text>
                  </Alert>
                );
              }

              const notesCandidateSupportDocsList = [
                ...getCandidateSupportDocs(notesAttachmentsData),
                ...getNotesData(notesAttachmentsData),
              ];

              const sortedNotesAttachments = notesCandidateSupportDocsList.sort(
                (a, b) => {
                  const dateA =
                    (a.__typename === 'ApplicationAttachmentV2' &&
                      a.uploadedDate) ||
                    (a.__typename === 'ApplicationNoteV2' && a.createdDate);

                  const dateB =
                    (b.__typename === 'ApplicationAttachmentV2' &&
                      b.uploadedDate) ||
                    (b.__typename === 'ApplicationNoteV2' && b.createdDate);

                  if (dateA && dateB) {
                    return (
                      new Date(dateB).getTime() - new Date(dateA).getTime()
                    );
                  }
                  return 0;
                },
              );

              return (
                <>
                  {(notesResponseError || attachmentResponseError) && (
                    <Box paddingTop="xsmall" paddingBottom="xsmall">
                      <Notice tone="critical">
                        <Text size="xsmall">
                          {t(
                            notesResponseError
                              ? "We can't show your notes right now. Try refreshing the page or check back later."
                              : "We can't show your attachments right now. Try refreshing the page or check back later.",
                          )}
                        </Text>
                      </Notice>
                    </Box>
                  )}

                  {notesCandidateSupportDocsList.length > 0 && (
                    <NotesAttachmentList
                      sortedNotesAttachments={sortedNotesAttachments}
                      locale={locale}
                      isVerifiedId={isVerifiedId}
                      jobId={jobId}
                      applicationCorrelationId={applicationCorrelationId}
                      onDeleteAttachment={onDeleteAttachment}
                      onDeleteNote={onDeleteNote}
                      userId={userId}
                      jobApplicationId={jobApplicationId}
                      seekCandidateId={seekCandidateId}
                      candidateId={adcentreCandidateId}
                    />
                  )}
                </>
              );
            })()}
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

export default observer(NotesAttachments);
